import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusBadge from "@/components/common/StatusBadge";
import { ArrowLeft, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  BUILDING_TYPE_LABELS, BUILDING_STATUS_LABELS, BUILDING_STATUS_TONES,
  OWNERSHIP_REGIME_LABELS,
} from "@/constants/patrimoine";
import BuildingSynthesisTab from "./tabs/BuildingSynthesisTab";
import BuildingLotsTab from "./tabs/BuildingLotsTab";
import BuildingChargesTab from "./tabs/BuildingChargesTab";
import BuildingFinancingsTab from "./tabs/BuildingFinancingsTab";
import BuildingDocumentsTab from "./tabs/BuildingDocumentsTab";
import BuildingHistoryTab from "./tabs/BuildingHistoryTab";
import ComingSoonInline from "./tabs/ComingSoonInline";
import { Coins, Hammer, Calendar } from "lucide-react";

export default function BuildingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const canDelete = ["owner", "admin"].includes(activeRole);
  const [building, setBuilding] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/buildings/${id}`);
      setBuilding(data);
      if (data.company_id) {
        try {
          const { data: c } = await api.get(`/companies/${data.company_id}`);
          setCompany(c);
        } catch {}
      }
    } catch { toast.error("Bâtiment introuvable"); navigate("/app/buildings"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement ce bâtiment et ses lots ?")) return;
    try {
      await api.delete(`/buildings/${id}`);
      toast.success("Bâtiment supprimé");
      navigate("/app/buildings");
    } catch { toast.error("Suppression impossible"); }
  };

  if (loading || !building) return <div className="text-muted-foreground text-sm">Chargement…</div>;

  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/app/buildings" data-testid="building-back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Retour aux bâtiments</Link>
        </Button>
      </div>

      <div className="card-elevated p-6 lg:p-8 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest">
              <span className="text-accent font-semibold">{BUILDING_TYPE_LABELS[building.building_type] || building.building_type}</span>
              {building.ownership_regime && <span className="text-muted-foreground">· {OWNERSHIP_REGIME_LABELS[building.ownership_regime]}</span>}
            </div>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">{building.name}</h1>
            <div className="mt-2 text-sm text-muted-foreground flex items-center gap-1">
              <MapPin className="h-4 w-4" /> {building.address}, {building.postal_code} {building.city}
            </div>
            {company && (
              <div className="mt-1 text-sm text-muted-foreground">
                Propriétaire : <Link to={`/app/companies/${company.id}`} className="text-accent link-underline">{company.denomination}</Link>
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <StatusBadge tone={BUILDING_STATUS_TONES[building.status] || "neutral"}>{BUILDING_STATUS_LABELS[building.status] || building.status}</StatusBadge>
              {building.year_built && <StatusBadge tone="neutral">Construction {building.year_built}</StatusBadge>}
              {building.cadastral_reference && <StatusBadge tone="neutral">Cadastre {building.cadastral_reference}</StatusBadge>}
            </div>
          </div>
          {canDelete && (
            <Button variant="outline" onClick={remove} data-testid="building-delete-btn">
              <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Supprimer
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="synthesis">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="synthesis" data-testid="building-tab-synthesis">Synthèse</TabsTrigger>
          <TabsTrigger value="lots" data-testid="building-tab-lots">Lots</TabsTrigger>
          <TabsTrigger value="finances" data-testid="building-tab-finances">Finances</TabsTrigger>
          <TabsTrigger value="credits" data-testid="building-tab-credits">Crédits</TabsTrigger>
          <TabsTrigger value="charges" data-testid="building-tab-charges">Charges</TabsTrigger>
          <TabsTrigger value="works" data-testid="building-tab-works">Travaux</TabsTrigger>
          <TabsTrigger value="documents" data-testid="building-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="photos" data-testid="building-tab-photos">Photos</TabsTrigger>
          <TabsTrigger value="echeances" data-testid="building-tab-echeances">Échéances</TabsTrigger>
          <TabsTrigger value="history" data-testid="building-tab-history">Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="synthesis"><BuildingSynthesisTab building={building} onSaved={load} /></TabsContent>
        <TabsContent value="lots"><BuildingLotsTab building={building} /></TabsContent>
        <TabsContent value="finances"><ComingSoonInline icon={Coins} title="Finances de l'immeuble"
          description="P&L par bâtiment, ratios de rentabilité et projections. Disponible dans le module Finances." /></TabsContent>
        <TabsContent value="credits"><BuildingFinancingsTab building={building} /></TabsContent>
        <TabsContent value="charges"><BuildingChargesTab building={building} /></TabsContent>
        <TabsContent value="works"><ComingSoonInline icon={Hammer} title="Travaux"
          description="Devis, chantiers en cours, garanties. Disponible dans le module Projets." /></TabsContent>
        <TabsContent value="documents"><BuildingDocumentsTab building={building} onlyImages={false} /></TabsContent>
        <TabsContent value="photos"><BuildingDocumentsTab building={building} onlyImages={true} /></TabsContent>
        <TabsContent value="echeances"><ComingSoonInline icon={Calendar} title="Échéances"
          description="Loyers, prêts, taxes et assurances. Disponible dans le module Échéances." /></TabsContent>
        <TabsContent value="history"><BuildingHistoryTab building={building} /></TabsContent>
      </Tabs>
    </div>
  );
}
